import type { DateInputProps } from '@healthnz-prm/fhir-engine-renderer/types'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Calendar03Icon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import { Input } from '@/components/ui/input'
import { InputFieldset } from '@/components/fhirEngineClinical/inputFieldset'
import { cn } from '@/lib/utils'

interface DateParts {
  day: string
  month: string
  year: string
}

function parseDateString(value: string | null | undefined): DateParts {
  if (!value) return { day: '', month: '', year: '' }

  const parts = value.split('-')
  const year = parts[0] ?? ''
  const month = parts[1] ?? ''
  const day = parts[2] ?? ''

  return {
    day: day === '00' ? '' : day.replace(/^0+(\d)/, '$1'),
    month: month === '00' ? '' : month.replace(/^0+(\d)/, '$1'),
    year: year === '0000' ? '' : year.replace(/^0+(\d+)/, '$1')
  }
}

function toFhirDateString(parts: DateParts, allowPartial: boolean): string | null {
  const { day, month, year } = parts

  if (!day && !month && !year) return null
  if (!year) return null

  const paddedYear = year.padStart(4, '0')

  if (!month && !day) {
    return allowPartial ? paddedYear : null
  }

  const paddedMonth = month.padStart(2, '0')

  if (!day) {
    return allowPartial ? `${paddedYear}-${paddedMonth}` : null
  }

  if (!month) {
    return allowPartial ? paddedYear : null
  }

  const paddedDay = day.padStart(2, '0')
  return `${paddedYear}-${paddedMonth}-${paddedDay}`
}

/**
 * Serialize date parts for non-partial mode, always emitting YYYY-MM-DD
 * (even if incomplete) so the renderer's validateDate can report the error.
 * Pads each component so the format matches FHIR expectations.
 */
function toFullDateString(parts: DateParts): string | null {
  const { day, month, year } = parts
  if (!day && !month && !year) return null
  const paddedYear = year ? year.padStart(4, '0') : ''
  const paddedMonth = month ? month.padStart(2, '0') : ''
  const paddedDay = day ? day.padStart(2, '0') : ''
  return `${paddedYear}-${paddedMonth}-${paddedDay}`
}

const SYNC_DEBOUNCE_MS = 400

/**
 * A hybrid date input: manual 3-field entry (DD/MM/YYYY) plus a native date picker.
 *
 * - Users can type directly into day/month/year fields
 * - Users can also click the native date picker to select a date visually
 * - Both methods stay in sync via shared local state
 * - Syncs to form state on blur or after a short debounce
 * - Outputs FHIR-compliant date strings (YYYY-MM-DD by default)
 * - When allowPartial is true, also emits YYYY or YYYY-MM per FHIR R4/R5 spec
 */
export default function DateInput(props: DateInputProps) {
  const {
    allowPartial = false,
    disabled,
    error: _error,
    inputId,
    label,
    onBlur,
    onChange,
    onFocus,
    value
  } = props

  const [localParts, setLocalParts] = useState<DateParts>(() =>
    parseDateString(value)
  )

  const isFocusedRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync external value into local state only when not focused
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalParts(parseDateString(value))
    }
  }, [value])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const syncToFormState = useCallback(
    (parts: DateParts) => {
      const fhirDate = allowPartial
        ? toFhirDateString(parts, true)
        : toFullDateString(parts)
      if (fhirDate !== value) {
        onChange(fhirDate)
      }
    },
    [allowPartial, onChange, value]
  )

  const scheduleDebouncedSync = useCallback(
    (parts: DateParts) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        syncToFormState(parts)
      }, SYNC_DEBOUNCE_MS)
    },
    [syncToFormState]
  )

  const handleFieldChange = useCallback(
    (field: keyof DateParts) => (e: ChangeEvent<HTMLInputElement>) => {
      const numericValue = e.target.value.replace(/\D/g, '')
      const maxLengths: Record<keyof DateParts, number> = {
        day: 2,
        month: 2,
        year: 4
      }
      const truncated = numericValue.slice(0, maxLengths[field])

      const newParts = { ...localParts, [field]: truncated }
      setLocalParts(newParts)
      scheduleDebouncedSync(newParts)
    },
    [localParts, scheduleDebouncedSync]
  )

  const handleFieldFocus = useCallback(() => {
    isFocusedRef.current = true
    onFocus?.()
  }, [onFocus])

  const handleFieldBlur = useCallback(() => {
    isFocusedRef.current = false

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    syncToFormState(localParts)
    onBlur?.()
  }, [localParts, syncToFormState, onBlur])

  const handlePickerChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const pickerValue = e.target.value
      if (!pickerValue) {
        const emptyParts: DateParts = { day: '', month: '', year: '' }
        setLocalParts(emptyParts)
        syncToFormState(emptyParts)
        onBlur?.()
        return
      }

      const newParts = parseDateString(pickerValue)
      setLocalParts(newParts)
      syncToFormState(newParts)
      onBlur?.()
    },
    [syncToFormState, onBlur]
  )

  const nativePickerValue =
    localParts.year && localParts.month && localParts.day
      ? `${localParts.year.padStart(4, '0')}-${localParts.month.padStart(2, '0')}-${localParts.day.padStart(2, '0')}`
      : ''

  return (
    <InputFieldset {...props} errorMessage={undefined}>
      <div className='flex items-center gap-2'>
        <div className='flex items-center gap-1'>
          <Input
            aria-label={`${label} day`}
            className='w-12 text-center'
            disabled={disabled}
            id={`${inputId}-day`}
            inputMode='numeric'
            maxLength={2}
            onChange={handleFieldChange('day')}
            onFocus={handleFieldFocus}
            onBlur={handleFieldBlur}
            placeholder='DD'
            type='text'
            value={localParts.day}
          />
          <span aria-hidden='true' className='text-muted-foreground'>/</span>
          <Input
            aria-label={`${label} month`}
            className='w-12 text-center'
            disabled={disabled}
            id={`${inputId}-month`}
            inputMode='numeric'
            maxLength={2}
            onChange={handleFieldChange('month')}
            onFocus={handleFieldFocus}
            onBlur={handleFieldBlur}
            placeholder='MM'
            type='text'
            value={localParts.month}
          />
          <span aria-hidden='true' className='text-muted-foreground'>/</span>
          <Input
            aria-label={`${label} year`}
            className='w-16 text-center'
            disabled={disabled}
            id={`${inputId}-year`}
            inputMode='numeric'
            maxLength={4}
            onChange={handleFieldChange('year')}
            onFocus={handleFieldFocus}
            onBlur={handleFieldBlur}
            placeholder='YYYY'
            type='text'
            value={localParts.year}
          />
        </div>
        {/* Native date picker — calendar popup alongside manual entry */}
        <div className='relative'>
          <input
            aria-label={`${label} date picker`}
            className='absolute inset-0 cursor-pointer opacity-0'
            disabled={disabled}
            onChange={handlePickerChange}
            tabIndex={-1}
            type='date'
            value={nativePickerValue}
          />
          <HugeiconsIcon
            icon={Calendar03Icon}
            aria-hidden
            className={cn(
              'size-4',
              disabled && 'opacity-50'
            )}
          />
        </div>
      </div>
    </InputFieldset>
  )
}
